/**
 * One name, submitted: a new group, a new site, a site's rename. The name is
 * trimmed and must not be empty; the container turns it into the request.
 */
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type NameDialogProps = {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly label: string;
  readonly initial: string;
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (name: string) => void;
  readonly onOpenChange: (open: boolean) => void;
};

function NameForm({ title, description, label, initial, submitLabel, pending, error, onSubmit, onOpenChange }: Omit<NameDialogProps, 'open'>) {
  const [name, setName] = useState(initial);
  const ready = name.trim() !== '' && name.trim().length <= 200;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && !pending) onSubmit(name.trim());
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="name-dialog-name">{label}</Label>
        <Input id="name-dialog-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="name-input" />
      </div>
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="name-submit">
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function NameDialog(props: NameDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <NameForm {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
